import React from 'react';
import { Box, Typography, useTheme, alpha, Chip } from '@mui/material';
import { formatPercentage, formatCurrency } from '@/utils/misc.ts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface FinancialInfoProps {
  grossReturnRental?: number;
  negotiatedPrice?: number;
  previousPrice?: number;
}

export const FinancialInfo: React.FC<FinancialInfoProps> = ({
  grossReturnRental,
  negotiatedPrice,
  previousPrice,
}) => {
  const theme = useTheme();

  const hasDiscount = previousPrice && negotiatedPrice && previousPrice > negotiatedPrice;
  const discountAmount = hasDiscount ? previousPrice - negotiatedPrice : 0;
  const discountPercentage = hasDiscount ? (discountAmount / previousPrice) * 100 : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.5,
        mt: 1.5,
        pt: 1.5,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
      }}
    >
      {/* Rentabilidad */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <TrendingUpIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.7 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.8,
            }}
          >
            Rentabilidad
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.2rem',
            color: theme.palette.success.main,
            lineHeight: 1.1,
          }}
        >
          {formatPercentage(grossReturnRental) ?? '-'}
        </Typography>
      </Box>

      {/* Precio */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <EuroIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.7 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.8,
            }}
          >
            Precio
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Current Price */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.2rem',
              color: 'text.primary',
              lineHeight: 1.1,
            }}
          >
            {formatCurrency(negotiatedPrice, 0)}
          </Typography>

          {/* Discount Badge - inline with previous price */}
          {hasDiscount && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  textDecoration: 'line-through',
                  lineHeight: 1,
                }}
              >
                {formatCurrency(previousPrice, 0)}
              </Typography>
              <Chip
                icon={<TrendingDownIcon sx={{ fontSize: '0.7rem !important' }} />}
                label={`-${discountPercentage.toFixed(1)}%`}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  '& .MuiChip-label': {
                    px: 0.4,
                  },
                  '& .MuiChip-icon': {
                    ml: 0.4,
                    mr: -0.25,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
